import { useContext, useMemo } from 'react';
import { useFormikContext } from 'formik';
import { SelectionAdapter } from 'components/SelectPropertiesDrawerV2';
import { AmplitudeContext } from 'utilities/amplitude/useAmplitude';

/**
 * Wires SelectPropertiesDrawerV2 to the AddYield BasicInfo Formik form.
 *
 * Writes the same field shape V1 wrote by default — full objects to
 * `sources`, IDs to `cropZones`, plus `totalArea` and `sourceType` — so
 * the existing reconciliation effect in BasicInfoSection keeps producing
 * the same result.
 *
 * existingCropZones is set from values.sources so V2 preserves per-zone
 * `area` on overlap, which BasicInfoSection re-sums into totalArea.
 *
 * Crop-scope filtering is driven by the `restrictToCropId` prop on the
 * drawer itself (passed at the call site from values.cropId) — the
 * adapter is purely Formik state-binding.
 *
 * Must be called inside a Formik context.
 */
const useAddYieldSelectionAdapter = (): SelectionAdapter => {
  const { values, setFieldValue } = useFormikContext<any>();
  const amplitude = useContext(AmplitudeContext);

  return useMemo(() => {
    // AddYield's Formik seeds `sources` with a placeholder `[{ id: '' }]`
    // before the user has picked anything. Drop entries without a real id
    // so the drawer doesn't render it as "1 selected crop zone" on open.
    const validSources = (values?.sources || []).filter((s: any) => !!s?.id);
    return {
      initialCropZones: validSources,
      existingCropZones: validSources,
      onConfirm: ({ cropZoneIds, totalArea, cropZones }) => {
        setFieldValue('sources', cropZones);
        setFieldValue('cropZones', cropZoneIds);
        setFieldValue('totalArea', totalArea);
        setFieldValue('sourceType', 'CROP_ZONE');
        amplitude.sendEventToAmplitude(
          amplitude.events.epic.YieldV2.addYield.saveProperties,
          { numberOfCrops: cropZones.length }
        );
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values?.sources]);
};

export default useAddYieldSelectionAdapter;
